import React from 'react'
import { Button } from '@/components/common/Button'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionTitle } from '@/components/common/SectionTitle'
import { useApiClient } from '@/hooks/useApiClient'
import { parseCSVFile, parseExcelFile } from '@/lib/fileParser'
import * as XLSX from 'xlsx'
import './Onboarding.css'

type Tipo = 'empresas' | 'personas'
type Preview = { total: number; validas: number; rechazadas: number; issues: Array<{ row: number; message: string }> }

export const Onboarding: React.FC = () => {
  const { makeClient } = useApiClient()
  const [tipo, setTipo] = React.useState<Tipo>('empresas')
  const [rows, setRows] = React.useState<Record<string, unknown>[]>([])
  const [headers, setHeaders] = React.useState<string[]>([])
  const [preview, setPreview] = React.useState<Preview | null>(null)
  const [notice, setNotice] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [fileName, setFileName] = React.useState<string | null>(null)

  const inspect = async (file: File) => {
    setBusy(true); setNotice(null); setPreview(null); setFileName(file.name)
    try {
      const parsed = file.name.toLowerCase().endsWith('.csv')
        ? await parseCSVFile(file)
        : await parseExcelFile(file, tipo === 'empresas' ? 'Empresas' : 'Personas')
      setRows(parsed.data); setHeaders(parsed.headers)
      const client = makeClient(); if (!client) throw new Error('Sin sesión')
      const response = await client.post<{ preview: Preview }>('/api/onboarding/import', { tipo, rows: parsed.data })
      setPreview(response.preview)
    } catch {
      setRows([]); setHeaders([]); setFileName(null); setNotice('No se pudo revisar el archivo. Usa un CSV o Excel con los campos indicados.')
    } finally { setBusy(false) }
  }

  const importRows = async () => {
    if (!preview || preview.rechazadas > 0 || rows.length === 0) return
    if (!window.confirm(`¿Confirmas incorporar ${preview.validas} ${tipo}? Esta acción crea registros en Wacner Sales.`)) return
    setBusy(true); setNotice(null)
    try {
      const client = makeClient(); if (!client) throw new Error('Sin sesión')
      const response = await client.post<{ imported: { count: number } }>('/api/onboarding/import', { tipo, rows, confirmar: true })
      setNotice(`Se incorporaron ${response.imported.count} registros.`)
      setRows([]); setHeaders([]); setPreview(null); setFileName(null)
    } catch { setNotice('No se realizó la importación. Revisa la previsualización y vuelve a intentarlo.') } finally { setBusy(false) }
  }

  const columns = tipo === 'empresas'
    ? 'nombre*; país, clasificación, segmento, dominios y nombre comercial son opcionales.'
    : 'nombre*, email*, empresa*, cargo, telefono, rol_en_relacion'

  const downloadTemplate = () => {
    const workbook = XLSX.utils.book_new()
    const companies = XLSX.utils.aoa_to_sheet([['nombre', 'pais', 'clasificacion', 'segmento', 'dominios', 'nombre_comercial']])
    const people = XLSX.utils.aoa_to_sheet([['nombre', 'empresa', 'email', 'cargo', 'telefono', 'rol_en_relacion']])
    const guide = XLSX.utils.aoa_to_sheet([
      ['Wacner Sales — plantilla de importación'],
      ['1. Completa primero la hoja Empresas. Sólo nombre es obligatorio; si no indicas país, se asigna Chile.'],
      ['2. Importa Empresas en Sales.'],
      ['3. Completa e importa Personas. Cada persona requiere una empresa que ya exista en Sales.'],
      ['4. No modifiques los encabezados de las hojas Empresas y Personas.'],
    ])
    XLSX.utils.book_append_sheet(workbook, companies, 'Empresas')
    XLSX.utils.book_append_sheet(workbook, people, 'Personas')
    XLSX.utils.book_append_sheet(workbook, guide, 'Guía')
    const blob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Plantilla_importacion_Wacner_Sales.xlsx'
    link.click()
    URL.revokeObjectURL(url)
  }

  return <div className="prototypePage">
    <PageHeader title="Importar datos" subtitle="Las empresas son el punto de partida. Cárgalas primero y después asocia sus personas." />
    <section className="prototypeSection">
      <SectionTitle title="Archivo de importación" subtitle="Descarga la matriz, carga primero empresas y luego personas asociadas. Nada se crea sin tu confirmación." />
      <div className="onboardingImport">
        <div className="onboardingImport__topline">
          <div className="onboardingImport__type" role="group" aria-label="Tipo de datos a importar">
            <Button size="sm" variant={tipo === 'empresas' ? 'primary' : 'secondary'} onClick={() => { setTipo('empresas'); setRows([]); setPreview(null); setFileName(null) }}>Empresas</Button>
            <Button size="sm" variant={tipo === 'personas' ? 'primary' : 'secondary'} onClick={() => { setTipo('personas'); setRows([]); setPreview(null); setFileName(null) }}>Personas</Button>
          </div>
          <div className="onboardingImport__templateActions">
            <Button size="sm" variant="secondary" onClick={downloadTemplate}>Descargar plantilla Excel</Button>
            <span className="onboardingImport__format">CSV o Excel</span>
          </div>
        </div>
        <p className="onboardingImport__fields"><strong>Campos:</strong> {columns}</p>
        {tipo === 'personas' && <p className="onboardingImport__rule"><strong>Regla:</strong> no se puede importar una persona sin una empresa ya creada en Sales.</p>}
        <label className="onboardingImport__file" aria-label="Seleccionar archivo CSV o Excel">
          <input className="onboarding-page__input" type="file" accept=".csv,.xlsx,.xls" disabled={busy} onChange={(event) => {
            const file = event.target.files?.[0]; if (file) void inspect(file)
          }} />
          <span className="onboardingImport__fileAction">{busy ? 'Revisando archivo…' : 'Seleccionar archivo'}</span>
          <span className="onboardingImport__fileMeta">{fileName ?? 'Ningún archivo seleccionado'}</span>
        </label>
        <p className="onboardingImport__hint">No se crean registros hasta que confirmes la revisión.</p>
      </div>
      {preview && <div className="prototypeContextCard" style={{ marginTop: 16 }}>
        <SectionTitle title="Revisión del archivo" subtitle={`${preview.validas} filas listas · ${preview.rechazadas} filas que requieren corrección.`} />
        {headers.length > 0 && <p className="prototypeRelationshipRow__summary">Columnas detectadas: {headers.join(', ')}</p>}
        {preview.issues.length > 0 && <ul className="onboarding-page__issues">{preview.issues.slice(0, 20).map((issue) => <li key={`${issue.row}-${issue.message}`}>Fila {issue.row}: {issue.message}</li>)}</ul>}
        <div className="prototypeContextCard__actions"><Button size="sm" disabled={busy || preview.validas === 0 || preview.rechazadas > 0} onClick={() => void importRows()}>Importar registros</Button></div>
      </div>}
      {notice && <p className="prototypeCommitmentForm__success" role="status">{notice}</p>}
    </section>
  </div>
}
